import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { bindOverflowCarousel } from '@theme/bt-scroll-carousel';

/**
 * @param {unknown} value
 * @returns {string}
 */
function stripMoneyHtml(value) {
  return String(value ?? '')
    .replace(/<[^>]*>/g, '')
    .trim();
}

/**
 * Drop .00 / ,00 when the amount is a whole number (matches Liquid format-price).
 * @param {string} formatted
 * @returns {string}
 */
function stripTrailingMoneyZeros(formatted) {
  return String(formatted).replace(/(\d)([.,])0{2}(?!\d)/g, '$1');
}

/**
 * Format money cents using Shopify money format when available.
 * Always returns plain text — currency apps often wrap formatMoney in HTML.
 * Whole amounts omit decimals (£40 not £40.00).
 * @param {number} cents
 * @param {string} [moneyFormat]
 * @returns {string}
 */
function formatMoney(cents, moneyFormat) {
  const amount = Number(cents) || 0;
  const format =
    stripMoneyHtml(moneyFormat) ||
    stripMoneyHtml(typeof Theme !== 'undefined' && Theme.moneyFormat) ||
    '{{amount}}';

  let formatted;
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    formatted = stripMoneyHtml(Shopify.formatMoney(amount, format));
  } else {
    const value = (amount / 100).toFixed(2);
    formatted = stripMoneyHtml(
      String(format)
        .replace(/\{\{\s*amount\s*\}\}/g, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amount / 100)))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
    );
  }

  return stripTrailingMoneyZeros(formatted);
}

/**
 * @param {HTMLElement} root
 * @returns {object | null}
 */
function readProductPayload(root) {
  const script = root.querySelector('script[data-stl-product-payload]');
  if (!(script instanceof HTMLScriptElement)) return null;
  try {
    return JSON.parse(script.textContent || '{}');
  } catch {
    return null;
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeSize(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Size of the most recently added line (cart items are newest first).
 * @param {Array<any>} items - Ajax cart line items
 * @returns {string}
 */
function sizeFromCartItems(items) {
  for (const item of items || []) {
    const option = (item?.options_with_values || []).find((entry) =>
      ['size', 'sizes'].includes(normalizeSize(entry?.name))
    );
    if (option?.value) return normalizeSize(option.value);
  }
  return '';
}

/** `null` = unknown; `''` = cart has no sized line. */
/** @type {string | null} */
let cartSizeCache = null;

/** @type {Promise<string> | null} */
let cartSizeRequest = null;

/**
 * Cached cart size; fetches `/cart.js` once when the cache is empty.
 * @returns {Promise<string>}
 */
function cartPreferredSize() {
  if (cartSizeCache !== null) return Promise.resolve(cartSizeCache);
  if (cartSizeRequest) return cartSizeRequest;

  cartSizeRequest = fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((res) => res.json())
    .then((cart) => {
      cartSizeCache = sizeFromCartItems(cart?.items);
      return cartSizeCache;
    })
    .catch((error) => {
      console.warn('[shop-the-look] Could not read cart size:', error);
      return '';
    })
    .finally(() => {
      cartSizeRequest = null;
    });
  return cartSizeRequest;
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  /** @type {any} */ (event).promise
    ?.then((/** @type {any} */ result) => {
      const items = result?.detail?.items;
      if (Array.isArray(items) && items.every((item) => Array.isArray(item?.options_with_values))) {
        cartSizeCache = sizeFromCartItems(items);
      } else {
        cartSizeCache = null;
        cartPreferredSize();
      }
    })
    .catch(() => {
      cartSizeCache = null;
    });
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) cartSizeCache = null;
});

/**
 * Find matching variant from selected options map.
 * @param {Array} variants
 * @param {Record<number, string>} selectedOptions
 */
function findVariant(variants, selectedOptions) {
  const matches = (variant) => {
    const o1 = selectedOptions[1];
    const o2 = selectedOptions[2];
    const o3 = selectedOptions[3];
    if (o1 != null && variant.option1 !== o1) return false;
    if (o2 != null && variant.option2 !== o2) return false;
    if (o3 != null && variant.option3 !== o3) return false;
    return true;
  };
  return variants.find((variant) => matches(variant) && variant.available) || variants.find(matches) || null;
}

/**
 * Batch-add variants to cart.
 * @param {HTMLElement} source
 * @param {Array<{id: string|number, quantity: number}>} items
 */
async function addItemsToCart(source, items) {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });

  const payload = {
    items: items.map((item) => ({
      id: Number(item.id),
      quantity: item.quantity || 1,
    })),
    sections: sectionIds.join(','),
  };

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 })),
      promise: deferredEventPromise.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then((res) => res.json());

    if (response.status) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: response.message || Theme.translations?.add_to_cart_error,
          code: 'INVALID',
          detail: {
            description: response.description,
            errors: response.errors,
          },
        })
      );

      const ajaxCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: true,
          items: ajaxCart.items,
          source: 'shop-the-look',
          sourceId: source.id?.toString?.() || '',
          itemCount: items.length,
        },
      });
      return { ok: false, message: response.description || response.message };
    }

    const ajaxCart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => res.json());

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'shop-the-look',
        sourceId: source.id?.toString?.() || '',
        itemCount: items.length,
        sections: response.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    deferredEventPromise.reject(error);
    console.warn('[shop-the-look] Add to cart failed:', error);
    return { ok: false, message: 'Could not add items' };
  }
}

/**
 * Section: horizontal looks track + scroll progress + pause + open drawer.
 *
 * @extends {Component}
 */
class ShopTheLookSection extends Component {
  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {import('./bt-scroll-carousel').OverflowCarouselBinding | null} */
  #carousel = null;

  connectedCallback() {
    super.connectedCallback();
    this.#mountPanels();
    this.#updateCount();
    this.#bindTrack();
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.#carousel?.disconnect();
    this.#carousel = null;
  }

  #looks() {
    return this.querySelectorAll('[data-shop-the-look-look]');
  }

  #updateCount() {
    const looks = this.#looks();

    this.classList.toggle('shop-the-look--overflow', looks.length > 4);

    const slider = this.querySelector('[data-stl-slider]');
    if (slider instanceof HTMLElement) {
      slider.hidden = looks.length <= 1;
    }
  }

  #mountPanels() {
    const drawer = this.querySelector('shop-the-look-drawer');
    const content = drawer?.querySelector('[data-stl-drawer-content]');
    if (!(content instanceof HTMLElement)) return;

    const panels = this.querySelectorAll('[data-stl-look-panel]');
    panels.forEach((panel) => {
      if (panel instanceof HTMLElement && drawer instanceof ShopTheLookDrawer) {
        drawer.syncPanelMeta(panel);
      }
    });
    content.replaceChildren(...panels);
  }

  #bindTrack() {
    const track = this.querySelector('[data-stl-track]');
    this.#carousel = bindOverflowCarousel({
      root: this,
      track: track instanceof HTMLElement ? track : null,
      prevButton: this.querySelector('[data-stl-prev]'),
      nextButton: this.querySelector('[data-stl-next]'),
      cardSelector: '.shop-the-look__look',
      progressVar: '--shop-the-look-progress',
      features: { progress: true, nav: true },
    });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const pauseBtn = target.closest('[data-shop-the-look-pause]');
    if (pauseBtn instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#togglePause(pauseBtn);
      return;
    }

    const openBtn = target.closest('[data-shop-the-look-open]');
    if (openBtn instanceof HTMLElement) {
      event.preventDefault();
      const lookId = openBtn.dataset.lookId;
      if (!lookId) return;
      this.#trigger = openBtn;
      const drawer = this.querySelector('shop-the-look-drawer');
      if (drawer instanceof ShopTheLookDrawer) {
        drawer.openLook(lookId, this.#trigger);
      }
    }
  };

  /** @param {HTMLElement} button */
  #togglePause(button) {
    const card = button.closest('[data-shop-the-look-card]');
    const video = card?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const playing = button.dataset.playing === 'true';
    const pauseIcon = button.querySelector('[data-icon-pause]');
    const playIcon = button.querySelector('[data-icon-play]');

    if (playing) {
      video.pause();
      button.dataset.playing = 'false';
      button.setAttribute('aria-label', Theme.translations?.play_video || 'Play video');
      pauseIcon?.setAttribute('hidden', '');
      playIcon?.removeAttribute('hidden');
    } else {
      video.play().catch(() => {});
      button.dataset.playing = 'true';
      button.setAttribute('aria-label', Theme.translations?.pause_video || 'Pause video');
      playIcon?.setAttribute('hidden', '');
      pauseIcon?.removeAttribute('hidden');
    }
  }
}

/**
 * Quick-view drawer for a look.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} content
 *
 * @extends {DialogComponent}
 */
class ShopTheLookDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  /** @type {HTMLElement | null} */
  #activePanel = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {Map<HTMLElement, Record<number, string>>} */
  #selectedByProduct = new Map();

  /** @type {import('./bt-scroll-carousel').OverflowCarouselBinding | null} */
  #heroCarousel = null;

  /** Bumped on every open/close so late cart responses are ignored. */
  #openToken = 0;

  /** Normalized size to preselect (PDP choice, else newest cart line). */
  #preferredSize = '';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener(DialogCloseEvent.eventName, this.#onClosed);
    if (cartSizeCache === null && this.dataset.cartSize !== undefined) {
      cartSizeCache = normalizeSize(this.dataset.cartSize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onClosed);
    this.#heroCarousel?.disconnect();
    this.#heroCarousel = null;
  }

  #onClosed = () => {
    if (this.#trigger instanceof HTMLElement) {
      this.#trigger.focus();
    }
    this.#activePanel = null;
    this.#openToken += 1;
    this.#heroCarousel?.disconnect();
    this.#heroCarousel = null;
  };

  /**
   * @param {string} lookId
   * @param {HTMLElement} [trigger]
   */
  openLook(lookId, trigger) {
    const panels = this.refs.content.querySelectorAll('[data-stl-look-panel]');
    let found = null;
    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const match = panel.dataset.lookId === lookId;
      panel.hidden = !match;
      if (match) found = panel;
    });

    if (!found) return;

    this.#activePanel = found;
    this.#trigger = trigger || null;
    this.#openToken += 1;
    const token = this.#openToken;
    this.#preferredSize = this.#pdpSelectedSize() || cartSizeCache || '';
    this.syncPanelMeta(found);
    this.#bindPanel(found);
    this.#bindHero(found);
    this.#updateBundleTotals(found);
    this.showDialog();

    if (!this.#preferredSize && cartSizeCache === null) this.#applyCartSize(found, token);
  }

  /**
   * Size the shopper picked on the PDP this drawer lives in.
   * @returns {string}
   */
  #pdpSelectedSize() {
    const checked = this.closest('bt-product-information')?.querySelector(
      '[data-bt-pdp-size-group] input[type="radio"]:checked'
    );
    return checked instanceof HTMLInputElement ? normalizeSize(checked.value) : '';
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} token
   */
  async #applyCartSize(panel, token) {
    const size = await cartPreferredSize();
    if (!size || token !== this.#openToken || this.#activePanel !== panel) return;

    this.#preferredSize = size;
    panel.querySelectorAll('[data-stl-product]').forEach((productEl) => {
      if (!(productEl instanceof HTMLElement)) return;
      this.#autoSelectSizes(productEl);
      this.#updateProductVariant(productEl);
    });
    this.#updateBundleTotals(panel);
  }

  /**
   * Hero gallery: look media first (when rendered), then every image of each product in row order.
   * @param {HTMLElement} panel
   */
  #bindHero(panel) {
    this.#heroCarousel?.disconnect();
    this.#heroCarousel = null;

    const hero = panel.querySelector('[data-stl-hero]');
    const track = hero?.querySelector('[data-stl-hero-track]');
    if (!(hero instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

    track.querySelectorAll('[data-stl-hero-added]').forEach((slide) => slide.remove());
    panel.querySelectorAll('[data-stl-product] template[data-stl-hero-slides]').forEach((template) => {
      if (template instanceof HTMLTemplateElement) track.append(template.content.cloneNode(true));
    });
    track.scrollLeft = 0;

    const slideCount = track.querySelectorAll('.shop-the-look-drawer__hero-slide').length;
    const progress = hero.querySelector('[data-stl-hero-progress]');
    if (progress instanceof HTMLElement) progress.hidden = slideCount <= 1;

    this.#heroCarousel = bindOverflowCarousel({
      root: hero,
      track,
      prevButton: hero.querySelector('[data-stl-hero-prev]'),
      nextButton: hero.querySelector('[data-stl-hero-next]'),
      cardSelector: '.shop-the-look-drawer__hero-slide',
      progressVar: '--stl-hero-progress',
      features: { progress: true, nav: true },
    });
    this.#heroCarousel.update();
  }

  /**
   * Count rendered look products and refresh the items label.
   * @param {HTMLElement} panel
   */
  syncPanelMeta(panel) {
    const count = panel.querySelectorAll('[data-stl-product]').length;
    panel.dataset.productCount = String(count);

    const label = panel.querySelector('[data-items-label]');
    if (label instanceof HTMLElement) {
      const template = count === 1 ? panel.dataset.itemsLabelOne : panel.dataset.itemsLabelOther;
      label.textContent = String(template || '')
        .replace(/\d+/, String(count))
        .trim();
    }

    const empty = panel.querySelector('[data-stl-empty]');
    if (empty instanceof HTMLElement) {
      empty.hidden = count > 0;
    }
  }

  /**
   * A product is ready to add when every required option (size) is chosen
   * and a purchasable variant is resolved.
   * @param {HTMLElement} productEl
   * @returns {boolean}
   */
  #isProductReady(productEl) {
    if (productEl.dataset.requiresSize === 'true') {
      const selected = this.#selectedByProduct.get(productEl) || {};
      const groups = productEl.querySelectorAll('[data-size-group]');
      const allSized = [...groups].every((group) => {
        if (!(group instanceof HTMLElement)) return false;
        const position = Number(group.dataset.optionPosition || 0);
        return Boolean(position && selected[position]);
      });
      if (!allSized) return false;
    }

    return Boolean(productEl.dataset.variantId) && productEl.dataset.available === 'true';
  }

  /**
   * Paint size selected state for one option group.
   * @param {HTMLElement} productEl
   * @param {number} position
   * @param {string} [value]
   * @param {boolean} [auto] - Picked by the drawer, not the shopper; cleared on the next open
   */
  #paintSizeGroup(productEl, position, value, auto = false) {
    const selected = this.#selectedByProduct.get(productEl) || {};
    if (value) {
      selected[position] = value;
    } else {
      delete selected[position];
    }
    this.#selectedByProduct.set(productEl, selected);

    productEl.querySelectorAll('[data-size-group]').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;
      if (Number(group.dataset.optionPosition || 0) !== position) return;
      group.querySelectorAll('[data-size-action]').forEach((el) => {
        const match = Boolean(value) && el.dataset.optionValue === value;
        el.toggleAttribute('data-selected', match);
        el.toggleAttribute('data-auto-selected', match && auto);
        el.setAttribute('aria-checked', match ? 'true' : 'false');
        el.classList.toggle('shop-the-look-drawer__size--selected', match);
        el.classList.toggle('text-p4-bold', match);
      });
    });
  }

  /**
   * Fill empty size groups with the shopper's size when it is in stock, or the
   * only size of a one-size product. Never fall back to the first size.
   * @param {HTMLElement} productEl
   */
  #autoSelectSizes(productEl) {
    productEl.querySelectorAll('[data-size-group]').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;
      const position = Number(group.dataset.optionPosition || 0);
      if (!position) return;

      const selected = this.#selectedByProduct.get(productEl) || {};
      if (selected[position]) return;

      const buttons = [...group.querySelectorAll('[data-size-action]')].filter(
        (btn) => btn instanceof HTMLButtonElement
      );
      const available = buttons.filter((btn) => !btn.disabled && btn.dataset.available !== 'false');
      const preferred = this.#preferredSize
        ? available.find((btn) => normalizeSize(btn.dataset.optionValue) === this.#preferredSize)
        : null;
      const pick = preferred || (buttons.length === 1 ? available[0] : null);

      if (pick instanceof HTMLButtonElement && pick.dataset.optionValue) {
        this.#paintSizeGroup(productEl, position, pick.dataset.optionValue, true);
      }
    });
  }

  /** @param {HTMLElement} panel */
  #bindPanel(panel) {
    this.#selectedByProduct.clear();

    panel.querySelectorAll('[data-stl-product]').forEach((productEl) => {
      if (!(productEl instanceof HTMLElement)) return;

      /** @type {Record<number, string>} */
      const selected = {};
      productEl.querySelectorAll('[data-colour-action][data-selected], [data-size-action][data-selected]').forEach((btn) => {
        if (!(btn instanceof HTMLElement)) return;
        if (btn.hasAttribute('data-auto-selected')) return;
        const position = Number(btn.dataset.optionPosition || 0);
        const value = btn.dataset.optionValue || '';
        if (position && value) selected[position] = value;
      });
      this.#selectedByProduct.set(productEl, selected);

      productEl.querySelectorAll('[data-size-group]').forEach((group) => {
        if (!(group instanceof HTMLElement)) return;
        const position = Number(group.dataset.optionPosition || 0);
        if (position) this.#paintSizeGroup(productEl, position, selected[position] || '');
      });

      this.#refreshSizeAvailability(productEl);
      this.#autoSelectSizes(productEl);
      this.#updateProductVariant(productEl);
    });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const colourBtn = target.closest('[data-colour-action]');
    if (colourBtn instanceof HTMLElement) {
      event.preventDefault();
      const productEl = colourBtn.closest('[data-stl-product]');
      if (productEl instanceof HTMLElement) this.#selectColour(productEl, colourBtn);
      return;
    }

    const sizeBtn = target.closest('[data-size-action]');
    if (sizeBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      if (sizeBtn.disabled) return;
      const productEl = sizeBtn.closest('[data-stl-product]');
      if (productEl instanceof HTMLElement) this.#selectSize(productEl, sizeBtn);
      return;
    }

    const wishlist = target.closest('[data-wishlist]');
    if (wishlist instanceof HTMLElement) {
      event.preventDefault();
      wishlist.dispatchEvent(
        new CustomEvent('wishlist:toggle', {
          bubbles: true,
          detail: { productId: wishlist.dataset.productId, button: wishlist },
        })
      );
      return;
    }

    const addAll = target.closest('[data-stl-add-all]');
    if (addAll instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#handleAddAll(addAll);
    }
  };

  /**
   * @param {HTMLElement} productEl
   * @param {HTMLElement} button
   */
  #selectColour(productEl, button) {
    const position = Number(button.dataset.optionPosition || 0);
    const value = button.dataset.optionValue || '';
    if (!position || !value) return;

    const selected = this.#selectedByProduct.get(productEl) || {};
    selected[position] = value;
    this.#selectedByProduct.set(productEl, selected);

    productEl.querySelectorAll('[data-colour-action]').forEach((el) => {
      const match = el === button;
      el.toggleAttribute('data-selected', match);
      el.setAttribute('aria-checked', match ? 'true' : 'false');
    });

    this.#refreshSizeAvailability(productEl);
    this.#autoSelectSizes(productEl);
    this.#updateProductVariant(productEl);
    if (this.#activePanel) this.#updateBundleTotals(this.#activePanel);
  }

  /**
   * @param {HTMLElement} productEl
   * @param {HTMLButtonElement} button
   */
  #selectSize(productEl, button) {
    const position = Number(button.dataset.optionPosition || 0);
    const value = button.dataset.optionValue || '';
    if (!position || !value) return;

    const alreadySelected = button.hasAttribute('data-selected');
    this.#paintSizeGroup(productEl, position, alreadySelected ? '' : value);
    this.#updateProductVariant(productEl);
    if (this.#activePanel) this.#updateBundleTotals(this.#activePanel);
  }

  /** @param {HTMLElement} productEl */
  #refreshSizeAvailability(productEl) {
    const payload = readProductPayload(productEl);
    const variants = payload?.variants || [];
    const selected = this.#selectedByProduct.get(productEl) || {};

    productEl.querySelectorAll('[data-size-group]').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;
      const position = Number(group.dataset.optionPosition || 0);
      let selectedStillAvailable = false;

      group.querySelectorAll('[data-size-action]').forEach((btn) => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const value = btn.dataset.optionValue || '';
        const trial = { ...selected, [position]: value };
        const match = findVariant(variants, trial);
        const available = Boolean(match?.available);
        btn.disabled = !available;
        btn.dataset.available = available ? 'true' : 'false';
        btn.classList.toggle('shop-the-look-drawer__size--unavailable', !available);
        if (available && btn.hasAttribute('data-selected')) selectedStillAvailable = true;
        if (match?.id) btn.dataset.variantId = String(match.id);
      });

      if (!selectedStillAvailable) {
        this.#paintSizeGroup(productEl, position, '');
      }
    });
  }

  /**
   * @param {HTMLElement} productEl
   * @param {object} variant
   * @param {string} moneyFormat
   * @param {number} discount
   */
  #paintProductPrice(productEl, variant, moneyFormat, discount) {
    const priceEl = productEl.querySelector('[data-price]');
    const compareEl = productEl.querySelector('[data-compare]');
    const listPrice =
      variant.compareAtPrice && variant.compareAtPrice > variant.price
        ? variant.compareAtPrice
        : variant.price;
    const salePrice = discount > 0 ? Math.round(variant.price * (1 - discount / 100)) : variant.price;

    if (priceEl) {
      priceEl.textContent =
        discount > 0 ? formatMoney(salePrice, moneyFormat) : variant.priceFormatted || formatMoney(variant.price, moneyFormat);
    }
    if (compareEl instanceof HTMLElement) {
      if (listPrice > salePrice) {
        compareEl.textContent =
          variant.compareAtPriceFormatted && discount <= 0
            ? variant.compareAtPriceFormatted
            : formatMoney(listPrice, moneyFormat);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  /** @param {HTMLElement} productEl */
  #updateProductVariant(productEl) {
    const payload = readProductPayload(productEl);
    const variants = payload?.variants || [];
    const selected = this.#selectedByProduct.get(productEl) || {};
    const variant = findVariant(variants, selected);
    const moneyHost = this.closest('[data-money-format]');
    const moneyFormat = moneyHost instanceof HTMLElement ? moneyHost.dataset.moneyFormat : '';
    const discount = Number(this.#activePanel?.dataset.bundleDiscount || 0);
    const sizeRequired = productEl.dataset.requiresSize === 'true';
    const sizeComplete =
      !sizeRequired ||
      [...productEl.querySelectorAll('[data-size-group]')].every((group) => {
        if (!(group instanceof HTMLElement)) return false;
        const position = Number(group.dataset.optionPosition || 0);
        return Boolean(position && selected[position]);
      });

    const links = productEl.querySelectorAll('[data-details-link]');

    if (variant && sizeComplete) {
      productEl.dataset.variantId = String(variant.id);
      productEl.dataset.variantPrice = String(variant.price);
      productEl.dataset.available = variant.available ? 'true' : 'false';
      this.#paintProductPrice(productEl, variant, moneyFormat, discount);
      links.forEach((link) => {
        if (link instanceof HTMLAnchorElement && variant.url) {
          link.href = variant.url;
        }
      });
    } else {
      delete productEl.dataset.variantId;
      productEl.dataset.available = 'false';
      if (variant) {
        productEl.dataset.variantPrice = String(variant.price);
        this.#paintProductPrice(productEl, variant, moneyFormat, discount);
        links.forEach((link) => {
          if (link instanceof HTMLAnchorElement && variant.url) {
            link.href = variant.url;
          }
        });
      }
    }
  }

  /** @param {HTMLElement} panel */
  #updateBundleTotals(panel) {
    const discount = Number(panel.dataset.bundleDiscount || 0);
    const moneyHost = this.closest('[data-money-format]');
    const moneyFormat = moneyHost instanceof HTMLElement ? moneyHost.dataset.moneyFormat : '';
    const products = [...panel.querySelectorAll('[data-stl-product]')].filter(
      (el) => el instanceof HTMLElement
    );
    const selectedProducts = products.filter((el) => this.#isProductReady(el));

    let originalSum = 0;
    selectedProducts.forEach((productEl) => {
      originalSum += Number(productEl.dataset.variantPrice || 0);
    });

    const allReady = products.length > 0 && selectedProducts.length === products.length;
    const noneReady = selectedProducts.length === 0;
    const bundleTotal = discount > 0 ? Math.round(originalSum * (1 - discount / 100)) : originalSum;
    const showCompare = allReady && discount > 0 && originalSum > bundleTotal;

    const labelEl = panel.querySelector('[data-stl-add-label]');
    const pricesWrap = panel.querySelector('[data-stl-add-prices]');
    const dashEl = panel.querySelector('[data-stl-add-dash]');
    const subtotalEl = panel.querySelector('[data-stl-subtotal]');
    const totalEl = panel.querySelector('[data-stl-total]');
    const addBtn = panel.querySelector('[data-stl-add-all]');

    if (noneReady) {
      if (labelEl) labelEl.textContent = panel.dataset.labelSelectSizes || 'select sizes';
      if (pricesWrap instanceof HTMLElement) pricesWrap.hidden = true;
      if (dashEl instanceof HTMLElement) dashEl.hidden = true;
      if (addBtn instanceof HTMLButtonElement) {
        addBtn.disabled = true;
        addBtn.classList.add('shop-the-look-drawer__add-all--pending');
      }
      return;
    }

    if (allReady) {
      if (labelEl) labelEl.textContent = panel.dataset.labelAddAll || 'add all to bag';
    } else {
      const count = selectedProducts.length;
      const template = count === 1 ? panel.dataset.labelAddOne : panel.dataset.labelAddOther;
      if (labelEl) {
        labelEl.textContent = String(template || 'add {{ count }} items to bag')
          .replace(/\{\{\s*count\s*\}\}/g, String(count))
          .replace(/\d+/, String(count))
          .trim();
      }
    }

    const showPrices = originalSum > 0;
    if (pricesWrap instanceof HTMLElement) pricesWrap.hidden = !showPrices;
    if (dashEl instanceof HTMLElement) dashEl.hidden = !showPrices;
    if (subtotalEl instanceof HTMLElement) {
      subtotalEl.textContent = showCompare ? formatMoney(originalSum, moneyFormat) : '';
      subtotalEl.hidden = !showCompare;
    }
    if (totalEl) totalEl.textContent = showPrices ? formatMoney(bundleTotal, moneyFormat) : '';
    if (addBtn instanceof HTMLButtonElement) {
      addBtn.disabled = false;
      addBtn.classList.remove('shop-the-look-drawer__add-all--pending');
    }
  }

  /** @param {HTMLButtonElement} button */
  async #handleAddAll(button) {
    const panel = this.#activePanel;
    if (!panel) return;

    const errorEl = panel.querySelector('[data-stl-error]');
    if (errorEl instanceof HTMLElement) errorEl.hidden = true;

    /** @type {Array<{id: string, quantity: number}>} */
    const items = [];
    panel.querySelectorAll('[data-stl-product]').forEach((productEl) => {
      if (!(productEl instanceof HTMLElement)) return;
      if (!this.#isProductReady(productEl)) return;
      const id = productEl.dataset.variantId;
      if (id) items.push({ id, quantity: 1 });
    });

    if (!items.length) {
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = Theme.translations?.select_size || 'Select a size for each item';
        errorEl.hidden = false;
      }
      return;
    }

    button.disabled = true;
    const result = await addItemsToCart(button, items);
    button.disabled = false;

    if (!result.ok) {
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = result.message || 'Could not add items';
        errorEl.hidden = false;
      }
      this.#updateBundleTotals(panel);
      return;
    }

    await this.closeDialog();

    const cartDrawer = document.querySelector('cart-drawer-component, #cart-drawer, theme-drawer#cart-drawer');
    if (cartDrawer && typeof /** @type {any} */ (cartDrawer).open === 'function') {
      /** @type {any} */ (cartDrawer).open();
    } else {
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  }
}

if (!customElements.get('shop-the-look-section')) {
  customElements.define('shop-the-look-section', ShopTheLookSection);
}

if (!customElements.get('shop-the-look-drawer')) {
  customElements.define('shop-the-look-drawer', ShopTheLookDrawer);
}
